import { throttle } from "es-toolkit/compat";
import type { Role } from "src/pods/realtime/util/role";

const INITIAL_PING_TIMEOUT = 1000;
const THROTTLE_PING_INTERVAL = 5000;

type PeerAction = "toolkit:peer-enter" | "toolkit:peer-depart" | "toolkit:peer-ping" | "toolkit:peer-pong";

type PeerState = {
  hasPeers: boolean;
};

type PublishFn = (topic: string, message: { action: PeerAction; payload: Record<string, unknown> }) => void;

type PeerDetectionOptions = {
  role: Role;
  userId: string;
  clientId: string;
  publish: PublishFn;
  onPeerChange: (state: PeerState) => void;
};

export function isPeerAction(action: string): action is PeerAction {
  return (
    action === "toolkit:peer-enter" ||
    action === "toolkit:peer-depart" ||
    action === "toolkit:peer-ping" ||
    action === "toolkit:peer-pong"
  );
}

export function createPeerDetection({ role, userId, clientId, publish, onPeerChange }: PeerDetectionOptions) {
  const peers: Record<string, number> = {};
  let hasPingedForHosts = false;
  let pingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;

  const topic = `user.${userId}`;

  function mutatePeers(fn: () => void) {
    const pre = Object.keys(peers).length;
    fn();
    const post = Object.keys(peers).length;
    if (pre !== post) onPeerChange({ hasPeers: post > 0 });
  }

  function addPeer(senderId: string) {
    mutatePeers(() => {
      peers[senderId] = Date.now();
    });
  }

  function removePeer(senderId: string) {
    mutatePeers(() => {
      delete peers[senderId];
    });
  }

  function publishPeerMessage(action: PeerAction, extraPayload?: Record<string, unknown>) {
    if (destroyed) return;
    publish(topic, {
      action,
      payload: { senderId: clientId, ...extraPayload },
    });
  }

  const checkForHosts = throttle(
    () => {
      if (hasPingedForHosts || destroyed) return;
      hasPingedForHosts = true;

      publishPeerMessage("toolkit:peer-ping");

      pingTimeoutId = setTimeout(() => {
        hasPingedForHosts = false;
        pingTimeoutId = null;
      }, INITIAL_PING_TIMEOUT);
    },
    THROTTLE_PING_INTERVAL,
    { leading: true, trailing: false },
  );

  function handleMessage(action: PeerAction, payload: Record<string, unknown>) {
    if (destroyed) return;

    const senderId = typeof payload?.senderId === "string" ? payload.senderId : null;

    if (!senderId || senderId === clientId) return;

    if (role === "host") {
      if (action === "toolkit:peer-ping") {
        publishPeerMessage("toolkit:peer-pong");
      }
    }

    if (role === "remote" || role === "display") {
      if (action === "toolkit:peer-pong" || action === "toolkit:peer-enter") {
        addPeer(senderId);
      }

      if (action === "toolkit:peer-depart") {
        removePeer(senderId);
      }

      // Compat: a host_dojo ping means an old host is present
      if (action === "toolkit:peer-ping" && /host_dojo/.test(senderId)) {
        addPeer(senderId);
      }
    }
  }

  function handleNatsEvent(event: { action: string; payload: Record<string, unknown> }) {
    if (!isPeerAction(event.action)) return;
    handleMessage(event.action, event.payload);
  }

  function start() {
    if (role === "host") {
      publishPeerMessage("toolkit:peer-enter", { role: "host" });
    }

    if (role === "remote" || role === "display") {
      checkForHosts();
    }
  }

  const beforeUnloadHandler = () => {
    if (role === "host") {
      publishPeerMessage("toolkit:peer-depart", { role: "host" });
    }
  };

  function destroy() {
    checkForHosts.cancel();

    if (pingTimeoutId !== null) {
      clearTimeout(pingTimeoutId);
      pingTimeoutId = null;
    }

    beforeUnloadHandler();
    destroyed = true;
    window.removeEventListener("beforeunload", beforeUnloadHandler);
  }

  // Register beforeunload for graceful depart
  window.addEventListener("beforeunload", beforeUnloadHandler);

  return {
    start,
    handleNatsEvent,
    destroy,
    hasPeers: () => Object.keys(peers).length > 0,
    getPeerCount: () => Object.keys(peers).length,
  };
}
